import type { OnboardingChecklistItem, OnboardingStatusResponse } from '../../../../types/features/storeDetails/onboarding';

export const getOnboardingOperationalReadinessItems = (onboardingData: OnboardingStatusResponse): OnboardingChecklistItem[] => [
    {
        label: 'Scratchers added to POS pricebook',
        value: onboardingData.setup.scratchers_added_to_pos_pricebook,
        field: 'scratchers_added_to_pos_pricebook',
        editable: true,
        help: 'Each scratcher game should be added in POS with its UPC before sale. When the state releases new games, those UPCs should be added too.',
    },
    {
        label: 'Lotto (Item) added to the POS for Draw Games',
        value: onboardingData.setup.lotto_item_added_to_pos_for_draw_games,
        field: 'lotto_item_added_to_pos_for_draw_games',
        editable: true,
        help: 'Add a dedicated Lotto item in POS for Draw Games (no UPC needed). Cashiers use this item when recording draw ticket sales.',
    },
    {
        label: 'Lotto + Scratcher Payout category added to POS',
        value: onboardingData.setup.lotto_scratcher_payout_category_added_to_pos,
        field: 'lotto_scratcher_payout_category_added_to_pos',
        editable: true,
        help: 'Use a dedicated Lotto Payout category in POS for Lotto and scratcher payouts, including free-ticket wins.',
    },
];

export const getOnboardingPosIntegrationItems = (onboardingData: OnboardingStatusResponse): OnboardingChecklistItem[] => [
    {
        label: `POS type selected: ${onboardingData.setup.pos_type || '-'}`,
        value: onboardingData.setup.pos_type_selected,
        help: 'POS type determines mapping and integration flow for your store.',
    },
    {
        label: `Backoffice agent configured${onboardingData.setup.backoffice_agent_note ? ` (${onboardingData.setup.backoffice_agent_note})` : ''}`,
        value: onboardingData.setup.backoffice_agent_configured,
        help: 'Some POS setups require a backoffice agent on a local machine. For POS where it is not needed, this is marked not applicable.',
    },
    {
        label: 'POS data available on LottoReco',
        value: onboardingData.setup.pos_data_available_on_lottoreco,
        field: 'pos_data_available_on_lottoreco',
        editable: true,
        help: 'Confirms that POS sales and payout data is reaching LottoReco for reconciliation.',
    },
    {
        label: `Store Closing Time configured: ${onboardingData.setup.store_closing_time || '-'}`,
        value: onboardingData.setup.store_closing_time_configured,
        help: 'Store closing time aligns day boundaries so sales, payouts, and inventory are reconciled on the correct business day.',
    },
];

export const getOnboardingStateLotteryIntegrationItem = (onboardingData: OnboardingStatusResponse): OnboardingChecklistItem => ({
    label: 'LottoReco invited to retailer portal',
    value: onboardingData.setup.lottoreco_invited_to_retailer_portal,
    help: 'LottoReco can pull state lottery reports only after your store invites our server account in your state lottery portal.',
    detail: `Invite email: ${onboardingData.setup.invite_email || '-'}`,
});

export const getOnboardingDailyOperationsItems = (onboardingData: OnboardingStatusResponse): OnboardingChecklistItem[] => [
    {
        label: 'Draw games: Recorded correctly in the POS at the time of sale',
        value: onboardingData.operations.draw_games_recorded_in_pos_at_sale,
        field: 'draw_games_recorded_in_pos_at_sale',
        help: 'Enter each draw game sale in POS at sale time using the dedicated Lotto item.',
    },
    {
        label: 'Scratchers: All scratchers are scanned in the POS using the UPC code at the time of sale.',
        value: onboardingData.operations.scratchers_scanned_by_upc_at_sale,
        field: 'scratchers_scanned_by_upc_at_sale',
        help: 'Scan each scratcher by UPC at checkout so sales tie correctly to game and inventory.',
    },
    {
        label: 'Payouts: All lottery payouts are reported in the Lotto Payout category at the time of payouts in the POS',
        value: onboardingData.operations.payouts_reported_in_lotto_payout_category,
        field: 'payouts_reported_in_lotto_payout_category',
        help: 'Record all lotto and scratcher payouts in Lotto Payout, including free-ticket wins.',
    },
    {
        label: 'Audits: End-of-day / shift-closing audits started',
        value: onboardingData.operations.audits_started_end_of_shift_day,
        field: 'audits_started_end_of_shift_day',
        help: 'Run scratcher inventory audits at shift/day close to catch and fix mismatches quickly.',
    },
    {
        label: 'Vending Machine: Close vending machine shift and enter draw ticket sales into POS at end of day.',
        value: onboardingData.operations.vending_shift_closed_and_draw_sales_entered,
        field: 'vending_shift_closed_and_draw_sales_entered',
        help: 'At day close, close vending shift and enter draw sales from vending into POS to keep totals accurate.',
    },
];
